import { Navigate, useSearchParams } from 'react-router';
import { DISCORD_LOGIN_URL, useMe } from '../lib/auth';

/** Contract's suggested dev identities (WP2-auth.md) — real sessions, no Discord round-trip. */
const DEV_IDENTITIES = [
  { label: 'host', discordId: '100000000000000001', username: 'dev-host' },
  { label: 'guest', discordId: '100000000000000002', username: 'dev-guest' },
  { label: 'admin', discordId: '100000000000000003', username: 'dev-admin' },
] as const;

/**
 * Turns the `?error=<code>` the api's OidcLoginFailureHandler redirects with into something a
 * person can act on. `authorization_request_not_found` is the one that actually happens in the
 * wild: the Discord callback landed in a browser that never started the sign-in — typically the
 * Discord desktop app took over the "Authorize" step and handed the redirect to the system's
 * default browser instead of the one the user was in — or a second attempt overwrote the first.
 * A bare `?error` (no code) keeps the generic line.
 */
export function signInFailureMessage(code: string | null): string {
  switch (code) {
    case 'authorization_request_not_found':
      return 'Discord sent you back to a browser that did not start this sign-in. Start and finish in the same browser: if the Discord desktop app opened to authorize, close it and approve on discord.com in this window instead.';
    case 'access_denied':
      return 'You cancelled the Discord authorization. Try again and choose Authorize.';
    default:
      return 'Discord sign-in did not complete. Please try again.';
  }
}

/**
 * The only way in. Discord OpenID Connect is the sole credential path — there is no password
 * field anywhere in this app, by design. The "Continue with Discord" anchor deliberately keeps
 * Discord's own brand blurple rather than the app's lime accent — it's a third-party identity
 * affordance, and recoloring it to match our theme would just make it look less trustworthy.
 */
export function Login() {
  const [searchParams] = useSearchParams();
  const { data: me, isPending } = useMe();

  if (isPending) {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <p className="telemetry text-text-faint">Checking session…</p>
      </div>
    );
  }

  if (me) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="relative min-h-full">
      <div className="atmosphere" />

      <div className="relative z-10 mx-auto max-w-6xl px-5 py-16 md:px-10 md:py-24">
        <div className="reveal flex items-baseline gap-4" style={{ ['--i' as string]: 0 }}>
          <span className="eyebrow">00 / Sign in</span>
          <span className="h-px flex-1 bg-border" aria-hidden="true" />
        </div>

        <h1
          className="display reveal mt-4 text-[clamp(3.5rem,10vw,8rem)]"
          style={{ ['--i' as string]: 1 }}
        >
          Strylander Interview Room
        </h1>

        <p className="reveal mt-4 max-w-prose text-lg text-text-muted" style={{ ['--i' as string]: 2 }}>
          Collaborative class-interview rooms for the MapleStory community.
        </p>

        {searchParams.has('error') && (
          <p
            role="alert"
            className="reveal mt-6 max-w-sm border-l-4 border-signal bg-bg-elevated px-4 py-3 text-sm text-text"
            style={{ ['--i' as string]: 3 }}
          >
            {signInFailureMessage(searchParams.get('error'))}
          </p>
        )}

        <div className="reveal mt-8 max-w-sm" style={{ ['--i' as string]: 4 }}>
          {/* A plain anchor, not a router link: this leaves the SPA for discord.com and returns
              through Caddy to /login/oauth2/code/discord. */}
          <a
            href={DISCORD_LOGIN_URL}
            className="flex min-h-tap w-full items-center justify-center gap-2 rounded-md bg-[#5865f2] px-4 text-base font-semibold text-white transition-colors hover:bg-[#4752c4]"
          >
            Continue with Discord
          </a>
          <p className="mt-3 text-xs text-text-faint">
            We only request the <code>openid</code> and <code>identify</code> scopes — your
            Discord id, username and avatar. No email, no passwords.
          </p>
        </div>

        {import.meta.env.DEV && (
          <div className="reveal mt-12 max-w-sm" style={{ ['--i' as string]: 5 }}>
            <span className="eyebrow">DEV · fake login</span>
            <div className="mt-3 flex flex-wrap gap-2">
              {DEV_IDENTITIES.map((identity) => (
                <a
                  key={identity.label}
                  href={`/api/dev/login?discordId=${identity.discordId}&username=${identity.username}`}
                  className="telemetry flex min-h-tap items-center rounded-md border border-border-strong px-3 text-text-muted transition-colors hover:border-accent-border hover:text-text"
                >
                  {identity.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
